import { Field } from '@/components/Fields';
import { useAppDispatch } from '@/hooks';
import { Button, Center, Flex, FormControl, Heading, Text, VStack } from '@chakra-ui/react';
import Link from 'next/link';
import { Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';
import { forgot } from './actions';

interface ForgotRequest {
  email: string;
}

export const ForgotView: React.FC = () => {
  const dispatch = useAppDispatch();
  const [isError, setError] = useState(false);
  const [isSuccessSend, setSuccessSend] = useState(false);
  const handleSubmit = async (
    values: ForgotRequest,
    { setSubmitting }: FormikHelpers<ForgotRequest>,
  ) => {
    setSubmitting(true);
    dispatch(
      forgot({
        formFields: [
          {
            id: 'email',
            value: values.email.toLocaleLowerCase(),
          },
        ],
      }),
    )
      .unwrap()
      .then((e: any) => {
        setSubmitting(false);
        if (e.status === 'OK') setSuccessSend(true);
        else setError(true);
      })
      .catch(() => {
        setSubmitting(false);
        setError(true);
      });
  };

  const forgotSchema = Yup.object().shape({
    email: Yup.string().min(2, 'Too Short!').email().max(60, 'Too Long!').required('Required'),
  });
  return (
    <Center>
      {isSuccessSend && (
        <VStack maxW={{ base: 'unset', md: '550px' }} alignItems="flex-start">
          <Heading fontSize="x-large">Thank you for submitting your request.</Heading>
          <Text>
            If the email address you entered is associated with an account in our system, we will
            send you an email with a link to reset your password.
          </Text>
          <Text>
            Please check your email inbox and follow the steps to regain access to your account.
          </Text>
          <Text>
            If you do not receive an email within the next few minutes, please ensure you entered
            the correct email address.
          </Text>
        </VStack>
      )}
      {!isSuccessSend && (
        <VStack gap={12}>
          <Heading>Reset password</Heading>
          <Formik
            initialValues={{ email: '' }}
            onSubmit={handleSubmit}
            validationSchema={forgotSchema}
          >
            {({ handleSubmit, isSubmitting, isValid, dirty, errors, touched }) => (
              <Form onSubmit={handleSubmit}>
                <FormControl alignItems="center" display="flex" flexDir="column">
                  <Field
                    type="email"
                    onDark
                    placeholder="Your Email"
                    name="email"
                    errors={errors.email}
                    touched={touched.email}
                    h="56px"
                    minW={{ base: 'unset', sm: '350px' }}
                    maxW="350px"
                    pl={3}
                    autoComplete="email"
                    full
                  />
                  {isError && (
                    <Text mb="30px" color="red" justifySelf="flex-start">
                      Oops! Something went wrong. Pleas try again.
                    </Text>
                  )}
                  <Button
                    fontWeight="semibold"
                    variant="primary"
                    size="xl"
                    w="full"
                    type="submit"
                    minW={{ base: 'unset', sm: '350px' }}
                    maxW="350px"
                    isDisabled={!isValid || !dirty || isSubmitting}
                    isLoading={isSubmitting}
                  >
                    Send reset link
                  </Button>
                </FormControl>
              </Form>
            )}
          </Formik>
          <Flex gap={1} flexDir={{ base: 'column', sm: 'row' }}>
            <Text>Want to sign in using old password?</Text>
            <Text as={Link} href="/" color="teal.500" cursor="pointer">
              Log in
            </Text>
          </Flex>
        </VStack>
      )}
    </Center>
  );
};
